import { useState, useEffect, useRef } from 'react';

import { cfetch } from '@confluence/network';

import type { IntegrationSpotlightTraits } from '../traits-helpers.tsx';

type TapTraitAttribute = {
	name: string;
	value: string;
};

type AtlassianAccountTraits = {
	attributes: TapTraitAttribute[] | null;
};

function parseIntegrationTraits(data: AtlassianAccountTraits | null): IntegrationSpotlightTraits {
	if (!data?.attributes) {
		return {};
	}

	const traits: IntegrationSpotlightTraits = {};
	data.attributes.forEach((attr) => {
		if (
			attr.name.startsWith('confluence_ui_integrationspotlight') ||
			attr.name.startsWith('confluence_ui_slack3pwithin1p') ||
			attr.name.startsWith('confluence_ui_mailto3pwithin1p')
		) {
			traits[attr.name] = attr.value;
		}
	});
	return traits;
}

/**
 * Fetch integration spotlight traits directly from the TAP personalization API.
 *
 * This is a standalone async function so it can be called lazily (e.g. on
 * first paste) without incurring a mount-time network request. The
 * `useSpotlightTraits` hook delegates to this for its own fetch.
 */
export const fetchIntegrationTraits = async (
	atlassianAccountId: string,
): Promise<IntegrationSpotlightTraits> => {
	const res = await cfetch(
		// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- Intentionally bypasses SSR cache: spotlight eligibility requires fresh data. See ACTIVATION-2527
		`/gateway/api/tap-delivery/api/v3/personalization/user/${atlassianAccountId}`,
		{ method: 'GET' },
	);
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	const data = (await res.json()) as AtlassianAccountTraits;
	return parseIntegrationTraits(data);
};

/**
 * Custom hook to fetch and process integration spotlight traits.
 *
 * Fetches directly from the TAP personalization API rather than using the
 * shared useAtlassianAccountTraits hook, because the shared hook may return
 * SSR-hydrated data that is stale for spotlight eligibility checks.
 */
export const useSpotlightTraits = (atlassianAccountId: string | null) => {
	const [integrationTraits, setIntegrationTraits] = useState<IntegrationSpotlightTraits>({});
	const [loading, setLoading] = useState(!!atlassianAccountId);
	const [error, setError] = useState<Error | null>(null);
	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (!atlassianAccountId || hasFetchedRef.current) {
			return;
		}

		hasFetchedRef.current = true;

		const fetchTraits = async () => {
			try {
				const traits = await fetchIntegrationTraits(atlassianAccountId);
				setIntegrationTraits(traits);
			} catch (err) {
				setError(err instanceof Error ? err : new Error(String(err)));
			} finally {
				setLoading(false);
			}
		};

		void fetchTraits();
	}, [atlassianAccountId]);

	return { integrationTraits, loading, error };
};
